import type { Context } from 'react'
import { createContext } from 'react'

/**
 * Cross-instance-safe replacement for React's `createContext`.
 *
 * Under Yarn PnP, a package with peer dependencies (like
 * `@twisto/design-system`) is virtualised into a SEPARATE copy per
 * dependent. Each copy re-evaluates the module that calls
 * `createContext`, producing a DISTINCT context object. A provider
 * rendered from one copy and a `useContext` consumer reached through
 * another copy then see different contexts — the consumer reads the
 * default value and throws "must be used within a <X>Provider".
 *
 * Keying the context on a stable string in a `globalThis` registry
 * guarantees every copy shares the same context object, regardless of
 * how many times the DS is instantiated (PnP virtuals, dual ESM/CJS,
 * or even multiple published versions). This is the standard pattern
 * React-ecosystem singletons use to survive "more than one copy" setups.
 *
 * SSR-safe: the registry holds context *definitions*, not request
 * state — provider values are still scoped per render.
 */
const REGISTRY_KEY = '__twisto_ds_shared_contexts__'

type Registry = Map<string, Context<unknown>>

const getRegistry = (): Registry => {
  const g = globalThis as unknown as Record<string, Registry | undefined>

  return (g[REGISTRY_KEY] ??= new Map())
}

export const createSharedContext = <T>(
  key: string,
  defaultValue: T
): Context<T> => {
  const registry = getRegistry()
  const existing = registry.get(key)
  if (existing) {
    return existing as Context<T>
  }
  const context = createContext<T>(defaultValue)
  registry.set(key, context as Context<unknown>)

  return context
}
